import { ANALYTIC_END_POINTS, DATA_ADAPTER_END_POINTS, DOCUMENT_END_POINTS, RESULT_END_POINTS } from './endpoints';
import { get, post } from './restClient';
import {
  DIL,
  DILList,
  Result,
  DILProperty,
  SRSValuesResult,
  SrsParameters,
  HistogramEndpointResponse,
  DILPropertyList,
  GeoJsonParams,
} from './types';
import { selectPropertyListFromDilByNames } from './singleton';

// TODO: Switch this to find instead of a for loop to simplify
export const findDilKeyByName = (DIL: DILList, name: string): string | null => {
  for (let key in DIL) {
    if (DIL[key].name === name) {
      return key;
    }
  }
  return null;
};

/** Execute a query against SRS */
export const query = async (entityDIL: DIL, query = {}): Promise<Result> => {
  const { dilId, dilDefinitionId } = entityDIL;
  return post<Result>(DATA_ADAPTER_END_POINTS.Query, {
    query: {
      [dilId]: {
        [dilDefinitionId]: query,
      },
    },
  });
};

/** Filters a result set to a smaller result set */
export const filter = async (result: Result, entityDIL: DIL, query: unknown = {}): Promise<Result> => {
  const { resultId } = result;
  const { dilId, dilDefinitionId } = entityDIL;
  return post<Result>(RESULT_END_POINTS.Filter(resultId), {
    query: {
      [dilId]: {
        [dilDefinitionId]: query,
      },
    },
  });
};

/**
 * Fetches specific data from a result set. Useful for getting lists of data
 * @param result The SRS result object that has the data you want
 * @param properties The list of properties desired from the result set. Use the `selectPropertyListFromDilByNames` function to get the array of DILProperties
 * @param options
 */
export const values = async <PropertyValues>(
  result: Result,
  properties: DILProperty[] | DILPropertyList,
  options: {
    startIndex?: number;
    sortProperty?: DILProperty;
    descending?: boolean;
    endIndex?: number;
    MaxValues?: number;
  } = {}
): Promise<SRSValuesResult<PropertyValues>[]> => {
  const { resultId, count } = result;
  const { startIndex = 0, sortProperty = null, descending = true, MaxValues = null } = options;
  let { endIndex } = options;
  const SortBy = sortProperty
    ? [
        {
          SortDirection: descending ? 'Descending' : 'Ascending',
          SortProperty: formatProperty(sortProperty),
        },
      ]
    : null;

  if (!endIndex) {
    endIndex = count - 1;
    if (startIndex !== 0) {
      console.warn(
        `No value was specified for endIndex, so ${endIndex} will be used. This will result in unexpected results because startIndex is greater than 0.`
      );
    }
  }

  return await post<SRSValuesResult<PropertyValues>[]>(RESULT_END_POINTS.Values(resultId), {
    propertyList: formatProperties(properties),
    parameters: {
      StartIndex: startIndex,
      EndIndex: endIndex,
      SortBy,
      MaxValues,
    },
  });
};

// Facets
//Keep this here just in case something breaks. This is the old one that will be deleted.
// TODO delete.
export const generateFacetsDEPRECATED = async (result: Result, properties: DILProperty[], parameters = {}) => {
  const { resultId } = result;
  return post(ANALYTIC_END_POINTS.Facets(resultId), {
    propertyList: properties.map(formatProperty),
    parameters,
  });
};

/**
 * Calls the facets endpoint
 * @param result
 * @param properties
 * @param parameters
 * @param dilName Optional param; if specified, will use all properties from the DIL in the metadata parameter
 */
export const generateFacets = async (
  result: Result,
  properties: DILProperty[],
  parameters: SrsParameters = {},
  dilName?: string
) => {
  const { resultId } = result;
  const { metadata, ...otherParams } = parameters;
  const fullMetadata = dilName && selectPropertyListFromDilByNames(dilName, metadata || []);

  return post(ANALYTIC_END_POINTS.Facets(resultId), {
    propertyList: properties.map(formatProperty),
    parameters: {
      parentResultId: result.parentId,
      ...otherParams,
      metadata: fullMetadata || metadata || [],
    },
  });
};

/**
 *
 * @param result
 * @param dilName
 * @param propertyNames
 * @param parameters
 */
export const generateHierarchicalFacets = async (
  result: Result,
  dilName: string,
  propertyNames: string[],
  parameters: SrsParameters = {}
) => {
  const { resultId } = result;
  const primaryProperty: DILProperty = selectPropertyListFromDilByNames(dilName, propertyNames)[0];
  const { metadata, ...otherParams } = parameters;
  const fullMetadata = selectPropertyListFromDilByNames(dilName, metadata || []);
  return post(ANALYTIC_END_POINTS.FacetsHierarchy(resultId), {
    property: primaryProperty,
    parameters: {
      parentResultId: result.parentId,
      ...otherParams,
      metadata: fullMetadata,
    },
  });
};

// This interface is highly likely to change
interface TextCluster {
  id: number;
  summaryTerms: string[];
  count: number;
}

// Since generateTextClusters
export const generateTextClusters = async (
  result: Result | number,
  properties: DILProperty[],
  parameters = { maxClusters: 15 }
): Promise<TextCluster[]> => {
  const resultId = typeof result === 'number' ? result : result.resultId;
  return post<TextCluster[]>(ANALYTIC_END_POINTS.TextClusters(resultId), {
    propertyList: properties.map(formatProperty),
    parameters,
  });
};

// GeneratePathHierarchy
export const generatePathHierarchy = async (result: Result, property: DILProperty, parameters: any = {}) => {
  const { resultId } = result;
  const { pathDepth = 20, parentPath = [], pathDelimiter = '/' } = parameters;
  return post(ANALYTIC_END_POINTS.PathHierarchy(resultId), {
    property: formatProperty(property),
    parameters: {
      pathDepth,
      parentPath,
      pathDelimiter,
    },
  });
};

// GenerateParallelCoordinates
// TODO: be more explicit with parameters
export const generateParallelCoordinates = async (
  result: Result,
  properties: Array<DILProperty>,
  parameters: any = {}
) => {
  const { resultId } = result;
  return post(ANALYTIC_END_POINTS.ParallelCoordinates(resultId), {
    propertyList: properties.map(formatProperty),
    parameters,
  });
};

/** Creates a hierarchy of data based on the passed in properties. Useful for building tree maps, circle packs or stacked bar charts. */
export const generatePropertyHierarchy = async (
  result: Result,
  properties: Array<DILProperty>,
  parameters: any = {}
) => {
  const { resultId } = result;
  const { JoinMethod = 'InnerJoin', ...otherParameters } = parameters;
  return post(ANALYTIC_END_POINTS.PropertyHierarchy(resultId), {
    propertyList: properties.map(formatProperty),
    parameters: {
      JoinMethod,
      ...otherParameters,
    },
  });
};

/** Generate Histogram */
export const buildHistogramTimeline = async (
  result: Result,
  properties: Array<DILProperty>,
  parameters: any = {}
): Promise<HistogramEndpointResponse> => {
  const { resultId } = result;
  const { MaxMajorBinCount = 10, MaxMinorBinCount = 100, ...otherParams } = parameters;
  return post(ANALYTIC_END_POINTS.Timeline(resultId), {
    propertyList: properties.map(formatProperty),
    parameters: {
      MaxMajorBinCount,
      MaxMinorBinCount,
      ...otherParams,
    },
  });
};

/**
 * Generate a GeoJson file from the given result
 * @param result
 * @param dilName
 * @param propertyNames The names of geometry properties to include
 * @param parameters
 */
export const generateGeoJson = async (
  result: Result,
  dilName: string,
  propertyNames: string[],
  parameters?: GeoJsonParams
) => {
  const geometryProperties = selectPropertyListFromDilByNames(dilName, propertyNames ?? []) ?? [];
  const metadataPropertes = selectPropertyListFromDilByNames(dilName, parameters?.metadataPropertyNames ?? []) ?? [];

  return await post(`/SRS/Analytics/Geospatial/${result.resultId}/GeoJSON`, {
    propertyList: geometryProperties.map(formatProperty),
    parameters: {
      ...parameters,
      metadata: metadataPropertes.map(formatProperty),
    },
  });
};

/** Fetch a document given it's documentId */
export const documentById = async (documentId: number, options = {}) => {
  return get(DOCUMENT_END_POINTS.GetDocumentById(documentId), options);
};

/** Fetch a document given it's dilId, dilDefinitionId, externalId */
export const documentByDilId = async (dilId: number, dilDefinitionId: number, externalId: number, options = {}) => {
  return get(DOCUMENT_END_POINTS.GetDocumentByExternalId(dilId, dilDefinitionId, externalId), options);
};

export const generateGraph = async (result: number | Result, properties: DILProperty[], parameters: SrsParameters) => {
  const resultId = typeof result === 'number' ? result : result.resultId;
  return post(ANALYTIC_END_POINTS.Graph(resultId), {
    propertyList: properties.map(formatProperty),
    parameters,
  });
};

// Private Utils
function formatProperties(properties: DILProperty[] | DILPropertyList): PropertyRequest[] {
  let propertyList: PropertyRequest[] = [];

  if (Array.isArray(properties)) {
    propertyList = properties.map(property => formatProperty(property));
  } else {
    for (const key in properties) {
      propertyList.push({
        systemDefinitionId: properties[key].systemDefinitionId,
        name: properties[key].name,
      });
    }
  }

  return propertyList;
}

function formatProperty(property: DILProperty): PropertyRequest {
  const { systemDefinitionId, name } = property;
  return {
    systemDefinitionId,
    name,
  };
}

interface PropertyRequest {
  systemDefinitionId: number;
  name: string;
}
